import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { heute, lang } from "../zeit";
import { MONATE, WOCHENTAG_KOPF, monatsblatt, monatVerschieben, plusTage, schnellwahl, tagMitWochentag } from "../kalender";
import { Schwebend } from "./Schwebend";
import s from "./auswahl.module.css";

export interface KalenderSprint {
  beginn: string;
  ende: string;
  name: string;
}

interface Props {
  /** YYYY-MM-DD oder null */
  wert: string | null;
  onWahl: (tag: string | null) => void;
  /** Wofür, zum Beispiel „Frist“ oder „Endet am“ (Name des Knopfs und des Fensters) */
  label: string;
  /** Text auf dem Knopf ohne Datum */
  leer?: string | undefined;
  /** Text zum Entfernen; ohne ihn lässt sich das Datum nicht leeren */
  entfernen?: string | undefined;
  /** Laufender Sprint: getönt, Sprintende mit Punkt und in der Schnellwahl */
  sprint?: KalenderSprint | null | undefined;
  /** Frühester wählbarer Tag */
  min?: string | undefined;
  gesperrt?: boolean | undefined;
  klein?: boolean | undefined;
  /** Zusatz unter dem Knopf, zum Beispiel „gilt Sprintende“ */
  zusatz?: string | undefined;
  /** Eigener Inhalt des Knopfs statt des Datums (Frist in der Liste: „Heute“, „überfällig“) */
  anzeige?: ReactNode | undefined;
  /** Eigener Name des Knopfs für Screenreader, zum Beispiel „Frist: Mo. 12.10., überfällig“ */
  knopfLabel?: string | undefined;
  knopfKlasse?: string | undefined;
}

/** Datum wählen (AUFGABEN-STUFE2.md, Abschnitt 6.4): Schnellwahl und Monatsblatt ab Montag. Kein natives Datumsfeld.
 *  Pfeiltasten bewegen den Tag, Enter wählt, Escape schließt und gibt den Fokus an den Knopf zurück. */
export function Kalender({ wert, onWahl, label, leer = "Datum wählen", entfernen, sprint, min, gesperrt, klein, zusatz, anzeige, knopfLabel, knopfKlasse }: Props) {
  const knopf = useRef<HTMLButtonElement>(null);
  const raster = useRef<HTMLDivElement>(null);
  const tag0 = heute();
  const [offen, setOffen] = useState(false);
  const [fokus, setFokus] = useState(wert ?? tag0);
  const [blatt, setBlatt] = useState<[number, number]>(() => [Number((wert ?? tag0).slice(0, 4)), Number((wert ?? tag0).slice(5, 7))]);

  function oeffnen() {
    const start = wert ?? tag0;
    setFokus(start);
    setBlatt([Number(start.slice(0, 4)), Number(start.slice(5, 7))]);
    setOffen(true);
    // Fokus auf den gewählten oder heutigen Tag, sobald das Blatt steht
    requestAnimationFrame(() => raster.current?.querySelector<HTMLButtonElement>(`[data-tag="${start}"]`)?.focus());
  }

  function waehlen(tag: string | null) {
    if (tag && min && tag < min) return;
    onWahl(tag);
    setOffen(false);
    knopf.current?.focus();
  }

  function bewegen(e: KeyboardEvent<HTMLDivElement>) {
    const schritte: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const n = schritte[e.key];
    if (n === undefined) return;
    e.preventDefault();
    const neu = plusTage(fokus, n);
    setFokus(neu);
    setBlatt([Number(neu.slice(0, 4)), Number(neu.slice(5, 7))]);
    requestAnimationFrame(() => raster.current?.querySelector<HTMLButtonElement>(`[data-tag="${neu}"]`)?.focus());
  }

  const [jahr, monat] = blatt;
  const wochen = monatsblatt(jahr, monat);
  const wahl = schnellwahl(tag0, sprint?.ende);

  return (
    <span className={s.kalenderFeld}>
      <button
        ref={knopf}
        type="button"
        className={[s.datumKnopf, klein && s.datumKlein, wert && s.datumGesetzt, knopfKlasse].filter(Boolean).join(" ")}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-label={knopfLabel ?? `${label}: ${wert ? lang(wert) : leer}. Ändern`}
        disabled={gesperrt}
        onClick={() => (offen ? setOffen(false) : oeffnen())}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
        {anzeige ?? (wert ? tagMitWochentag(wert) : leer)}
      </button>
      {zusatz ? <span className={s.datumZusatz}>{zusatz}</span> : null}
      <Schwebend anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} label={`${label} wählen`} breite={560}>
        <div className={s.kalender}>
          <ul className={s.schnell} aria-label="Schnellwahl">
            {wahl.map((w) => (
              <li key={w.label}>
                <button type="button" disabled={!!min && w.tag < min} onClick={() => waehlen(w.tag)}>
                  <span>{w.label}</span>
                  <span className={s.meta}>{tagMitWochentag(w.tag)}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className={s.monat}>
            <div className={s.monatKopf}>
              <strong aria-live="polite">
                {MONATE[monat - 1]} {jahr}
              </strong>
              <span className={s.monatKnoepfe}>
                <button
                  type="button"
                  className={s.textKnopf}
                  onClick={() => {
                    setFokus(tag0);
                    setBlatt([Number(tag0.slice(0, 4)), Number(tag0.slice(5, 7))]);
                  }}
                >
                  Heute
                </button>
                <button type="button" className={s.pfeil} aria-label="Vorheriger Monat" onClick={() => setBlatt(monatVerschieben(jahr, monat, -1))}>
                  ‹
                </button>
                <button type="button" className={s.pfeil} aria-label="Nächster Monat" onClick={() => setBlatt(monatVerschieben(jahr, monat, 1))}>
                  ›
                </button>
              </span>
            </div>
            <div ref={raster} className={s.raster} role="grid" aria-label={`${MONATE[monat - 1]} ${jahr}`} onKeyDown={bewegen}>
              <div role="row" className={s.zeile}>
                {WOCHENTAG_KOPF.map((w) => (
                  <span key={w} role="columnheader" className={s.wt}>
                    {w}
                  </span>
                ))}
              </div>
              {wochen.map((woche) => (
                <div role="row" key={woche[0]?.tag} className={s.zeile}>
                  {woche.map(({ tag, imMonat }) => {
                    const imSprint = !!sprint && tag >= sprint.beginn && tag <= sprint.ende;
                    const klassen = [
                      s.tag,
                      !imMonat && s.fremd,
                      imSprint && s.imSprint,
                      tag === tag0 && s.heute,
                      tag === sprint?.ende && s.sprintende,
                      tag === wert && s.gewaehlt,
                    ].filter(Boolean);
                    return (
                      <span role="gridcell" key={tag} aria-selected={tag === wert}>
                        <button
                          type="button"
                          data-tag={tag}
                          className={klassen.join(" ")}
                          tabIndex={tag === fokus ? 0 : -1}
                          disabled={!!min && tag < min}
                          aria-label={`${lang(tag)}${tag === tag0 ? ", heute" : ""}${tag === sprint?.ende ? `, Ende ${sprint.name}` : ""}`}
                          onClick={() => waehlen(tag)}
                          onFocus={() => setFokus(tag)}
                        >
                          {Number(tag.slice(8, 10))}
                        </button>
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
            <p className={s.legende}>
              <span className={s.legHeute} aria-hidden="true" /> heute
              {sprint ? (
                <>
                  <span className={s.legSprint} aria-hidden="true" /> {sprint.name}
                  <span className={s.legEnde} aria-hidden="true" /> Sprintende
                </>
              ) : null}
            </p>
            {wert && entfernen ? (
              <button type="button" className={s.textKnopf} onClick={() => waehlen(null)}>
                {entfernen}
              </button>
            ) : null}
          </div>
        </div>
      </Schwebend>
    </span>
  );
}
